import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Mail, 
  Phone, 
  Calendar,
  ArrowLeft,
  RefreshCw,
  Download,
  Search
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/registrations`);
      setRegistrations(response.data);
    } catch (error) {
      console.error("Error fetching registrations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchRegistrations();
    }
  }, [isAuthenticated, fetchRegistrations]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      alert("Contraseña incorrecta");
    }
  };

  const filteredRegistrations = registrations.filter(reg => 
    reg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.phone.includes(searchTerm)
  );

  const exportToCSV = () => {
    const headers = ["Nombre", "Email", "Teléfono", "Fecha de registro", "Estado"];
    const csvContent = [
      headers.join(","),
      ...filteredRegistrations.map(reg => 
        [
          `"${reg.name}"`,
          reg.email,
          reg.phone,
          new Date(reg.created_at).toLocaleDateString('es-AR'),
          reg.status
        ].join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `registros_tertulias_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center p-6">
        <Card className="w-full max-w-md card-soft">
          <CardHeader>
            <CardTitle className="font-serif text-2xl text-[#3D4A4F] text-center">
              Panel de Administración
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#3D4A4F] mb-2">
                  Contraseña
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresá la contraseña"
                  className="input-soft"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full btn-primary"
                data-testid="admin-login-btn"
              >
                Ingresar
              </Button>
              <Link to="/">
                <Button variant="ghost" className="w-full text-[#6B7B80]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al inicio
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <Link to="/" className="text-[#C47F68] hover:text-[#B06E58] flex items-center gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Volver al sitio
            </Link>
            <h1 className="font-serif text-3xl text-[#4A4036]">Panel de Administración</h1>
            <p className="text-[#786C60]">Gestión de registros del club</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={fetchRegistrations}
              variant="outline"
              className="border-[#C47F68] text-[#C47F68]"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button 
              onClick={exportToCSV}
              className="btn-primary"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="card-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C47F68]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#C47F68]" />
                </div>
                <div>
                  <p className="text-sm text-[#786C60]">Total registrados</p>
                  <p className="text-3xl font-bold text-[#4A4036]">{registrations.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#7FBDC5]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#7FBDC5]" />
                </div>
                <div>
                  <p className="text-sm text-[#786C60]">Registros hoy</p>
                  <p className="text-3xl font-bold text-[#4A4036]">
                    {registrations.filter(r => 
                      new Date(r.created_at).toDateString() === new Date().toDateString()
                    ).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#25D366]/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <p className="text-sm text-[#786C60]">Pendientes</p>
                  <p className="text-3xl font-bold text-[#4A4036]">
                    {registrations.filter(r => r.status === 'pending').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#786C60]" />
            <Input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-soft pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="registrations-table">
              <thead className="bg-[#F5F0EB] border-b border-[#E6DCD3]">
                <tr>
                  <th className="text-left p-4 font-medium text-[#4A4036]">Nombre</th>
                  <th className="text-left p-4 font-medium text-[#4A4036]">Email</th>
                  <th className="text-left p-4 font-medium text-[#4A4036]">Teléfono</th>
                  <th className="text-left p-4 font-medium text-[#4A4036]">Fecha</th>
                  <th className="text-left p-4 font-medium text-[#4A4036]">Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-[#786C60]">
                      Cargando...
                    </td>
                  </tr>
                ) : filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-[#786C60]">
                      No hay registros
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((reg, index) => (
                    <tr 
                      key={reg.id} 
                      className="border-b border-[#E6DCD3] hover:bg-[#F5F0EB]/50"
                      data-testid={`registration-row-${index}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#C47F68]/10 rounded-full flex items-center justify-center">
                            <span className="text-[#C47F68] font-medium">
                              {reg.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-[#4A4036]">{reg.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <a 
                          href={`mailto:${reg.email}`}
                          className="text-[#C47F68] hover:underline flex items-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          {reg.email}
                        </a>
                      </td>
                      <td className="p-4">
                        <a 
                          href={`https://wa.me/${reg.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#25D366] hover:underline flex items-center gap-2"
                        >
                          <Phone className="w-4 h-4" />
                          {reg.phone}
                        </a>
                      </td>
                      <td className="p-4 text-[#786C60]">
                        {new Date(reg.created_at).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="p-4">
                        {(() => {
                          const statusMap = {
                            pending: { classes: 'bg-yellow-100 text-yellow-700', label: 'Pendiente' },
                            paid: { classes: 'bg-green-100 text-green-700', label: 'Pagado' }
                          };
                          const status = statusMap[reg.status] || { classes: 'bg-gray-100 text-gray-700', label: reg.status };
                          return (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.classes}`}>
                              {status.label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Footer note */}
        <p className="text-center text-sm text-[#786C60] mt-8">
          Contraseña de acceso: <code className="bg-[#F5F0EB] px-2 py-1 rounded">tertulias2026</code>
          <br />
          <span className="text-xs">Podés cambiarla en el código del archivo AdminPage.jsx</span>
        </p>
      </div>
    </div>
  );
}
